import type { Lens } from "@/hooks/use-lens";
import type { WasteLineStats } from "@/hooks/use-line-stats";
import type { TFunction } from "@/i18n/catalog";
import { foodLabel } from "@/lib/food-labels";

export type ExamineMode = "dish" | "ingredient";

/** One row of the Examine-waste widget (POS2-876). */
export type ExamineRow = {
  lineKey: string;
  label: string;
  /** Current-period lens value. */
  value: number;
  /** Dish rows with a current-period capture link into The Proof; ingredient rows never do. */
  hasEvidence: boolean;
};

export type ExamineList = { rows: ExamineRow[]; total: number };

/** Top-N per the mock: 6 menu lines, 5 purchasing lines. */
const MODE_ROWS: Record<ExamineMode, number> = { dish: 6, ingredient: 5 };

function metric(line: WasteLineStats, i: number, lens: Lens): number {
  return lens === "weight" ? line.periods[i].total_weight : line.periods[i].total_cost;
}

function current(line: WasteLineStats, lens: Lens): number {
  return metric(line, line.periods.length - 1, lens);
}

/**
 * Dish mode = recipe-backed lines plus category-only lines labelled by their
 * category (ticket); ingredient mode = inventory-item (L4) lines. `remainder`
 * (an aggregate) and `unidentified` (can't carry a par or order change — this
 * panel is the action step) are in neither mode.
 */
function inMode(line: WasteLineStats, mode: ExamineMode): boolean {
  if (line.line_key === "remainder" || line.line_key === "unidentified") return false;
  const isItem = line.inventory_item_id != null;
  return mode === "ingredient" ? isItem : !isItem;
}

/**
 * The rows to list: the mode's lines ranked by current-period lens value,
 * zero-value lines dropped, capped per the mock. `total` sums the shown rows —
 * the footer says "Top … lines", so the number matches what's listed.
 */
export function selectExamineLines(
  lines: WasteLineStats[],
  mode: ExamineMode,
  lens: Lens,
): ExamineList {
  const rows = lines
    .filter((line) => inMode(line, mode))
    .map((line) => ({
      lineKey: line.line_key,
      label: line.label,
      value: current(line, lens),
      hasEvidence:
        mode === "dish" && line.periods[line.periods.length - 1].tagged_capture_count > 0,
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, MODE_ROWS[mode]);

  return { rows, total: rows.reduce((sum, row) => sum + row.value, 0) };
}

/**
 * The trigger micro-fragment — a template over streak counts, no free text:
 * "Trigger: {top} #1 {n} wks" (consecutive periods ending now where the top
 * line is the mode's max) plus up to two "{label} rising {n} wks" fragments
 * (values strictly rising ≥ 2 consecutive transitions ending now). Null when
 * the mode's lines have no events before the current period — with < 2 periods
 * of data there is no streak to report.
 */
export function examineTrigger(
  t: TFunction,
  lines: WasteLineStats[],
  mode: ExamineMode,
  lens: Lens,
): string | null {
  const modeLines = lines.filter((line) => inMode(line, mode));
  const hasHistory = modeLines.some((line) =>
    line.periods.slice(0, -1).some((p) => p.event_count > 0),
  );
  if (!hasHistory) return null;

  const top = modeLines.reduce<WasteLineStats | null>(
    (best, line) => (best == null || current(line, lens) > current(best, lens) ? line : best),
    null,
  );
  if (!top || current(top, lens) <= 0) return null;

  // Consecutive periods (ending now) where the top line leads the mode.
  let topStreak = 0;
  for (let i = top.periods.length - 1; i >= 0; i--) {
    const value = metric(top, i, lens);
    const leads =
      value > 0 && modeLines.every((line) => line === top || metric(line, i, lens) <= value);
    if (!leads) break;
    topStreak++;
  }

  const risers = modeLines
    .filter((line) => line !== top)
    .map((line) => {
      let streak = 0;
      for (
        let i = line.periods.length - 1;
        i > 0 && metric(line, i, lens) > metric(line, i - 1, lens);
        i--
      ) {
        streak++;
      }
      return { line, streak };
    })
    .filter((r) => r.streak >= 2)
    .sort((a, b) => b.streak - a.streak || current(b.line, lens) - current(a.line, lens))
    .slice(0, 2);

  // Each fragment is a keyed sentence with its own plural, and the line names go through
  // `foodLabel` like every other food name — the whole string was English before (POS2-1158).
  const parts = [
    t("dashboard.examineTriggerTop", {
      label: foodLabel(t, top.label),
      count: topStreak,
    }),
    ...risers.map((r) =>
      t("dashboard.examineTriggerRising", {
        label: foodLabel(t, r.line.label),
        count: r.streak,
      }),
    ),
  ];
  return t("dashboard.examineTriggerPrefix", { parts: parts.join(", ") });
}
